import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchCurrentPatient, getPatientDataFromProfile } from '../utils/api';
import { tokenStorage } from '../utils/tokenStorage';
import { validateUserData } from '../utils/helpers';
import { usePodIP } from './usePodIP';

/**
 * Custom hook for managing user data
 * @returns {Object} - User data state and methods
 */
export const useUserData = () => {
  const [userData, setUserData] = useState(() => {
    const profile = tokenStorage.getUserProfile();
    return getPatientDataFromProfile(profile);
  });
  const [loading, setLoading] = useState(!userData);
  const [error, setError] = useState(null);
  const { podIP } = usePodIP();
  const location = useLocation();

  // Watch for profile changes in storage (e.g., after login)
  // This is important because the profile might be stored after the component mounts
  useEffect(() => {
    const checkProfile = () => {
      const profile = tokenStorage.getUserProfile();
      if (!profile) {
        return;
      }
      
      const profileData = getPatientDataFromProfile(profile);
      if (!profileData) {
        return;
      }

      // If we have profile data, update it using functional update to avoid stale closures
      if (validateUserData(profileData)) {
        setUserData(prevUserData => {
          // Only update if the data is actually different
          if (!prevUserData || JSON.stringify(profileData) !== JSON.stringify(prevUserData)) {
            setLoading(false);
            return profileData;
          }
          return prevUserData;
        });
      }
    };

    // Check immediately - data should be stored after successful sign-in
    checkProfile();

    // Listen for custom event when profile is stored (dispatched from AuthCallback)
    const handleProfileStored = () => {
      checkProfile();
    };

    // Listen for storage events (for cross-tab scenarios)
    const handleStorageChange = (e) => {
      if (e.key === 'user_profile' || e.key === null) {
        checkProfile();
      }
    };

    // Listen for window focus (in case profile was stored while tab was inactive)
    const handleFocus = () => {
      checkProfile();
    };

    // Listen for visibility change (when tab becomes visible)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkProfile();
      }
    };

    window.addEventListener('userProfileStored', handleProfileStored);
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('userProfileStored', handleProfileStored);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []); // Empty dependency array - set up listeners once and keep them

  // Check profile when route changes (e.g., after navigation from callback)
  useEffect(() => {
    // Skip check on callback route
    if (location.pathname === '/auth/callback') {
      return;
    }

    // Check profile when navigating away from callback or on any route change
    const profile = tokenStorage.getUserProfile();
    if (!profile) {
      return;
    }
    
    const profileData = getPatientDataFromProfile(profile);
    if (profileData && validateUserData(profileData)) {
      setUserData(prevUserData => {
        if (!prevUserData || JSON.stringify(profileData) !== JSON.stringify(prevUserData)) {
          setLoading(false);
          return profileData;
        }
        return prevUserData;
      });
    }
  }, [location.pathname]);

  useEffect(() => {
    if (podIP) {
      const loadUserData = async () => {
        try {
          setLoading(true);
          setError(null);

          // Fetch current patient data from storage (fetchCurrentPatient doesn't need podIP)
          const data = await fetchCurrentPatient();

          if (data) {
            setUserData(data);
          } else {
            // If fetchCurrentPatient returns invalid data, try getting from storage directly
            const profile = tokenStorage.getUserProfile();
            const profileData = getPatientDataFromProfile(profile);
            if (validateUserData(profileData)) {
              setUserData(profileData);
            } else {
              throw new Error('Invalid user data received');
            }
          }
          // if (validateUserData(data)) {
          //   setUserData(data);
          // } else {
          //   throw new Error('Invalid user data received');
          // }
        } catch (err) {
          console.error('Error loading user data:', err);
          setError(err.message);
          // On error, still try to get from storage
          const profile = tokenStorage.getUserProfile();
          const profileData = getPatientDataFromProfile(profile);
          if (validateUserData(profileData)) {
            setUserData(profileData);
          }
        } finally {
          setLoading(false);
        }
      };

      loadUserData();
    } else {
      // If podIP is not available, try to get userData from storage
      const profile = tokenStorage.getUserProfile();
      const profileData = getPatientDataFromProfile(profile);
      if (profileData && validateUserData(profileData)) {
        setUserData(profileData);
      }
      setLoading(false);
    }
  }, [podIP]);

  const updateUserData = (newData) => {
    setUserData(prev => ({ ...prev, ...newData }));
  };

  const refreshUserData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch current patient data from storage
      const data = await fetchCurrentPatient();

      if (validateUserData(data)) {
        setUserData(data);
      } else {
        // If fetchCurrentPatient returns invalid data, try getting from storage directly
        const profile = tokenStorage.getUserProfile();
        const profileData = getPatientDataFromProfile(profile);
        if (validateUserData(profileData)) {
          setUserData(profileData);
        } else {
          throw new Error('Invalid user data received');
        }
      }
    } catch (err) {
      console.error('Error refreshing user data:', err);
      setError(err.message);
      // On error, still try to get from storage
      const profile = tokenStorage.getUserProfile();
      const profileData = getPatientDataFromProfile(profile);
      if (validateUserData(profileData)) {
        setUserData(profileData);
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    userData,
    loading,
    error,
    updateUserData,
    refreshUserData
  };
};
